import React, { useState } from 'react';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Storefront } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useCompany } from '../context/CompanyProvider';
import { KIOSK_PILOT_EMAIL } from '../constants/kiosk';
import CompanyWebsiteLink from '../components/CompanyWebsiteLink';

const KIOSK_PILOT_PASSWORD = 'Metals';

const Login = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectTo = searchParams.get('redirect') || '/';
  const sessionExpired = searchParams.get('session') === 'expired';
  const inactiveAccount = searchParams.get('reason') === 'inactive';
  const { login, isAuthenticated } = useAuth();
  const { company } = useCompany();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [kioskLoading, setKioskLoading] = useState(false);

  // Redirect if already authenticated (kiosk pilot goes to kiosk via App.js root route)
  if (isAuthenticated) {
    return <Navigate to={redirectTo.startsWith('/') ? redirectTo : '/'} replace />;
  }

  const handleKioskLogin = async () => {
    setKioskLoading(true);
    setError('');
    try {
      const result = await login(KIOSK_PILOT_EMAIL, KIOSK_PILOT_PASSWORD);
      if (result.success) {
        navigate('/time-tracking/kiosk', { replace: true });
      } else {
        setError(result.message || 'Kiosk login failed');
      }
    } catch (err) {
      setError('Could not sign in. Check that the kiosk user exists.');
    } finally {
      setKioskLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);

      if (!result.success) {
        setError(result.message);
      }
    } catch (err) {
      setError('Unable to sign in. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            component="img"
            src={company.logos.header}
            alt={company.shortName}
            sx={{ height: 56, width: 'auto', mb: 2, objectFit: 'contain' }}
          />
          <Typography component="h1" variant="h4" gutterBottom>
            {company.shortName}
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Sign in to your account
          </Typography>

          {inactiveAccount && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }}>
              Your account is inactive. Contact an administrator if you need access.
            </Alert>
          )}
          {sessionExpired && !inactiveAccount && (
            <Alert severity="info" sx={{ width: '100%', mb: 2 }}>
              Your session has expired. Sign in again to continue. Unsaved changes on this page were not saved.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 1 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
            <Typography variant="body2" sx={{ textAlign: 'center', mb: 1 }}>
              <Button size="small" onClick={() => navigate('/forgot-password')}>
                Forgot password?
              </Button>
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'center', mb: 2 }}>
              <Button size="small" onClick={() => navigate('/kiosk/activate-account')}>
                Create Login
              </Button>
            </Typography>

            <Divider sx={{ my: 2 }}>or</Divider>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Shop computer — clock in/out only
            </Typography>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Storefront />}
              onClick={handleKioskLogin}
              disabled={kioskLoading || loading}
              sx={{ mb: 2 }}
            >
              {kioskLoading ? <CircularProgress size={20} /> : 'Enter Kiosk Mode'}
            </Button>
            <Typography variant="caption" color="text.secondary" display="block">
              Uses saved login; opens time clock only.
            </Typography>
          </Box>

          {company.marketingSite?.url && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <CompanyWebsiteLink slot="login" />
            </Box>
          )}

        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
